import "./style.scss";

import { getValueOrDraft, shouldDraft } from "components/Beneficiar/utils";
import CreateTypology from "components/BeneficiaryFormContainer/CreateTypology";
import ModalBox from "components/Modal";
import NewForm from "components/NewForm";
import { handleScrollToErrors } from "components/NewForm/utils";
import type { TAddressAutococompleteOption } from "components/shared/AddressAutocomplete";
import { getAddressEditOption } from "components/shared/AddressAutocomplete";
import DeleteModal from "components/shared/DeleteModal";
import type { BeneficiaryAuxAccountMap } from "containers/Beneficiars/types";
import {
    extractEntityCustomFields,
    formatCustomFields,
    formatFieldsForDefaultValues,
} from "containers/CustomFieldsContainer/helpers";
import { socialProfileList } from "containers/NewBeneficiaryContainer";
import type { IFormStep } from "containers/TermFormContainer";
import { PortalType } from "contexts/portal.context";
import { getCountries } from "countries";
import { currenciesList } from "currenciesList";
import { resolveBankingStatus } from "helpers/bankingStatusResolver";
import { getCleanStartorEndDay, isObject } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import useUserRole from "hooks/useUserRole";
import { isValidBIC, isValidIBAN } from "ibantools";
import { useContext, useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchTypologies } from "store/accountingStore";
import { updateArticles } from "store/articleStore";
import type { TEntity } from "store/beneficiaryStore";
import {
    fetchCreateBeneficiary,
    fetchDeleteBeneficiary,
    setDeleteBeneficiaryIdleStatus,
    updateBeneficiary,
} from "store/beneficiaryStore";
import { fetchBrands } from "store/brandStore";
import { fetchCustomFields, ICustomField } from "store/customFieldsStore";
import type { IStatementTemplate } from "store/partnerStatementsStore";
import { fetchPartnerStatements } from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import { BeneficiaryVAT, EPortalType } from "store/types";

import AccountingPaymentStep from "./AccountingPaymentStep";
import AdressStep from "./AddressStep";
import PartnerStatementStep from "./PartnerStatementStep";
import PersonalInfoStep from "./PersonalInfoStep";
import ProfileStep, { vatOptions } from "./ProfileStep";
import type { BrandAccountingCode, TFormCreateBeneficiar } from "./types";
import { validateBankingMetadata } from "./utils";

interface ICreateBeneficiar {
    /**
     * This prop is the name of the component mode.
     */
    mode: "new" | "edit-page";
    /**
     * This is the state to display editMode
     */
    editModeActive?: boolean;
    /**
     * Close edit mode
     */
    closeEditMode?: () => void;
    handleModalClose?: (type: "publication" | "beneficiar", data: any) => Promise<void>;
}

const BeneficiaryFormContainer = ({ mode, editModeActive, closeEditMode, handleModalClose }: ICreateBeneficiar) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const portalType = useContext(PortalType);

    const { beneficiar, termAssociatedToBeneficiaries, deleteBeneficiaryStatus } = useAppSelector(
        (state) => state.beneficiary,
    );
    // Not making this change right now in case it involves other fixes, but use only companyInfos everywhere (it's there below):
    const companySettings = useAppSelector((state) => state.settings.companySettings);
    const { typologies, typologiesStatus } = useAppSelector((state) => state.accounting);
    const partnerStatements = useAppSelector((state) => state.partnerStatements.partnerStatements);
    const brands = useAppSelector((state) => state.brands.brands);
    const { periodsDateRange } = useAppSelector((state) => state.period);
    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);
    const { customFields: _customFields, customFieldsStatus } = useAppSelector((state) => state.customFields);
    const { userRole } = useUserRole();

    const currentCustomFields = useMemo(() => {
        return _customFields.filter((field) => field.relatedResource === "beneficiary");
    }, [_customFields]);

    const { companyInfos, getCompanySettings, isCompany, countryFeatures, companyCountryCode } = useCompanySettings();
    const [acceptedPayments, setAcceptedPayments] = useState<string[]>([]);
    const [accountingModal, setAccountingModal] = useState(false);
    const [newTypologies, setNewTypologies] = useState<string[]>([]);
    const [newTypologyName, setNewTypologyName] = useState("");
    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showRibError, setShowRibError] = useState(false);
    const [customFields, setCustomFields] = useState(currentCustomFields);

    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const alternativeCurrencies = JSON.parse(getCompanySettings("active_alternative_currencies"));
    const usesStatementTemplates = getCompanySettings("uses_custom_statement_templates");
    const usesBrands = getCompanySettings("uses_multiple_brands");
    const companyAcceptedPayments = getCompanySettings("accepted_payment_methods");
    const usesTwentyPercentVat = getCompanySettings("uses_twenty_percent_vat");

    const _vatOptions = vatOptions(countryFeatures.vat.options, usesTwentyPercentVat);

    const precountList = countryFeatures.precount.options.map((o) => ({ value: o.value, label: t(o.label) }));

    const isPaymentActive = (paymentType: "rib" | "paypal" | "check") => {
        return acceptedPayments.includes(paymentType);
    };

    const statementCurrency = currenciesList().find(
        (currency) => currency.code == (editModeActive ? beneficiar.statementCurrency : companyInfos?.currency),
    );

    const getCustomStatement = (statementId) =>
        partnerStatements.find((statement) => statement.id == statementId) ?? {};

    const formatEntity = (beneficiaryEntity: TEntity | undefined) => {
        if (beneficiaryEntity === "none") return undefined;
        else return beneficiaryEntity;
    };

    /** USEFORM INITIATE VALUES */
    const defaultFormValues: TFormCreateBeneficiar = useMemo(() => {
        const vat = _vatOptions.find((item) => item.value === +beneficiar?.vat);
        const defaultValues: TFormCreateBeneficiar = {
            avatarUrl: editModeActive ? beneficiar?.avatarUrl ?? "" : "",
            name: editModeActive ? beneficiar?.name ?? "" : "",
            surname: editModeActive ? beneficiar?.surname ?? "" : "",
            pseudonym: editModeActive ? beneficiar?.pseudonym ?? "" : "",
            // birthDate is stored backend-side as UTC midnight ("YYYY-MM-DDT00:00:00.000Z").
            // Rebuild a local Date from the UTC calendar parts so the picker shows the day
            // the user picked, not the previous day when they sit west of UTC.
            birthDate: editModeActive
                ? beneficiar?.birthDate
                    ? getCleanStartorEndDay(beneficiar.birthDate)
                    : null
                : null,
            email: editModeActive ? beneficiar?.email ?? "" : "",
            loginEmail: editModeActive ? beneficiar.loginEmail ?? null : null,
            phoneNumber: editModeActive ? getValueOrDraft(beneficiar?.phoneNumber) ?? "" : "",
            street: editModeActive ? getAddressEditOption(beneficiar) ?? "" : "",
            zipCode: editModeActive ? getValueOrDraft(beneficiar?.zipCode) ?? "" : "",
            city: editModeActive ? getValueOrDraft(beneficiar?.city) ?? "" : "",
            country: { name: editModeActive ? beneficiar?.country : "", flag: "" },
            socialProfileType: editModeActive
                ? socialProfileList[(companySettings?.distributorType ?? 0) - 1].find(
                      (socialProfile) => socialProfile.type === beneficiar?.socialProfileType,
                  ) ?? null
                : socialProfileList[0][0],
            precount: editModeActive
                ? precountList.find((o) => o.value === beneficiar?.precount) ?? precountList[0]
                : precountList[0],
            vat: editModeActive ? vat ?? _vatOptions[0] : _vatOptions[0],
            activityType: editModeActive ? beneficiar?.activityType ?? "" : "",
            socialSecurityNumber: editModeActive ? getValueOrDraft(beneficiar?.socialSecurityNumber) ?? "" : "",
            siretNumber: editModeActive ? getValueOrDraft(beneficiar?.siretNumber) ?? "" : "",
            beneficiaryCode: editModeActive ? beneficiar?.beneficiaryCode ?? "" : "",
            brandAccountingCode: editModeActive ? beneficiar?.brandAccountingCode ?? [] : [],
            withheldPercent: editModeActive ? Math.round(beneficiar?.withheldPercent ?? 0) / 100 : null,

            /** Payments */
            ribOwnerName: editModeActive ? beneficiar?.ribOwnerName : "",
            iban: editModeActive ? beneficiar?.iban ?? "" : "",
            swift: editModeActive ? beneficiar.swift ?? "" : "",
            bypassIbanValidation: editModeActive ? beneficiar.validBankInfos ?? 0 : 0,
            paypalEmail: editModeActive ? beneficiar?.paypalEmail : "",
            bankCheck: editModeActive ? beneficiar?.bankCheck : 0,
            accountingTypology: editModeActive ? (beneficiar?.accountingTypology as string) : typologies[0] ?? "",
            accountingCode: editModeActive ? beneficiar?.accountingCode : "",
            accountingCodeGroups:
                editModeActive && beneficiar?.accountingCodeGroups?.length
                    ? JSON.stringify(beneficiar.accountingCodeGroups)
                    : JSON.stringify([{ mainAccount: "", auxiliaryAccount: "" }]),
            statementCurrency: statementCurrency,
            customStatementTemplate: editModeActive
                ? (getCustomStatement(beneficiar?.customStatementTemplateId) as IStatementTemplate)
                : undefined,
            lang: editModeActive ? beneficiar?.lang : companyInfos?.countryCode?.startsWith("en") ? "en" : "fr",
            routingNumber: editModeActive ? beneficiar.routingNumber ?? "" : "",
            bankingMetadata:
                editModeActive && beneficiar?.bankingMetadata && !Array.isArray(beneficiar.bankingMetadata)
                    ? beneficiar.bankingMetadata
                    : {},
            entity: editModeActive ? formatEntity(beneficiar?.entity) || "physical" : "physical",
            bankingStatus: editModeActive ? beneficiar?.bankingStatus ?? "local" : "local",
            bankAccountCountry: (() => {
                const meta = editModeActive && beneficiar?.bankingMetadata;
                if (meta && !Array.isArray(meta)) {
                    const code = (meta as Record<string, string>).bank_account_country;
                    if (code) return getCountries().find((c) => c.code === code) ?? null;
                }
                // Foreign beneficiaries without an explicit bank_account_country: leave null so
                // bankingMode falls back to bankingStatus (foreign_local → international fields).
                const status = editModeActive ? beneficiar?.bankingStatus : null;
                if (status === "foreign_local" || status === "foreign_international") return null;
                return companyCountryCode ? getCountries().find((c) => c.code === companyCountryCode) ?? null : null;
            })(),
        };

        // If there are custom fields created, add them to the default values:
        if (currentCustomFields.length > 0) {
            const customFields = formatFieldsForDefaultValues({
                customFields: currentCustomFields,
                editModeActive,
                typeData: beneficiar,
            });
            defaultValues.customFields = customFields ?? [];
        }

        return defaultValues;
    }, [beneficiar, editModeActive, typologies, companyInfos]);

    useEffect(() => {
        /** refresh defaultFormValues while switching beneficiar to show data */
        if (mode === "edit-page") {
            reset(defaultFormValues);
            setShowRibError(false);
        }
    }, [editModeActive]);

    const methods = useForm<TFormCreateBeneficiar>({
        defaultValues: defaultFormValues,
        mode: "all",
    });

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        watch,
        trigger,
        setError,
        formState: { errors, isDirty, dirtyFields },
        reset,
    } = methods;

    const selectedTypology = watch("accountingTypology");
    const iban = watch("iban");
    const bic = watch("swift");
    const entityType = watch("entity");
    const watchedLoginEmail = watch("loginEmail");

    const isLoginEmailChanging = useMemo(() => {
        return editModeActive && watchedLoginEmail !== beneficiar?.loginEmail;
    }, [watchedLoginEmail, beneficiar?.loginEmail, editModeActive]);
    useEffect(() => {
        if (mode !== "edit-page" || !editModeActive) return;

        if (isLoginEmailChanging && watchedLoginEmail) {
            setValue("email", watchedLoginEmail);
        } else {
            setValue("email", beneficiar.email);
        }
    }, [mode, editModeActive, isLoginEmailChanging, watchedLoginEmail, beneficiar.email, setValue]);

    const hasPendingEmail = useMemo(() => {
        return !!beneficiar?.pendingEmail;
    }, [beneficiar]);

    // If companyInfos loads after the form mounts, fill in the default banking country
    // for local beneficiaries (new or existing without a saved bank_account_country).
    useEffect(() => {
        if (!companyInfos) return;
        if (getValues("bankAccountCountry")) return;
        if (getValues("bankingStatus") !== "local") return;
        const country = companyCountryCode ? getCountries().find((c) => c.code === companyCountryCode) ?? null : null;
        if (country) setValue("bankAccountCountry", country);
    }, [companyInfos]);

    // Displays custom fields according to entity type.
    useEffect(() => {
        if (entityType) {
            const filteredFields = extractEntityCustomFields(currentCustomFields, entityType);
            if (filteredFields) setCustomFields(filteredFields);
        }
    }, [currentCustomFields, entityType]);

    useEffect(() => {
        if (mode === "edit-page") {
            if (iban !== defaultFormValues.iban) setValue("bypassIbanValidation", false);
            if (bic !== defaultFormValues.swift) setValue("bypassIbanValidation", false);
        }
    }, [iban, bic]);

    /** Update state accepted payments on change beneficiar */
    useEffect(() => {
        updateAcceptedPayments();
    }, [beneficiar, editModeActive]);

    const updateAcceptedPayments = () => {
        setAcceptedPayments([]);
        /** format fetch data from " rib, check  paypal" to ['rib', 'check', 'paypal'] */
        if (beneficiar.acceptedPayments) {
            const formatAcceptedPayments: string[] = beneficiar?.acceptedPayments
                ?.trim()
                .split(/[, ]/)
                .filter((payment) => payment !== "");
            setAcceptedPayments((prevPayments) => [...prevPayments, ...formatAcceptedPayments]);
        }
    };

    /** ADD TYPOLOGIES SHOW MODAL & LOGIC */
    useEffect(() => {
        if ((selectedTypology as any)?.id == -1) {
            setAccountingModal(true);
            setValue("accountingTypology", "");
        }
    }, [selectedTypology]);

    useEffect(() => {
        if (newTypologyName === "") return;
        setValue("accountingTypology", newTypologies?.find((typology) => typology === newTypologyName) ?? "");
    }, [newTypologies, newTypologyName]);

    // Sets up typologies on first load. If "new", need to force setValue as accountingTypology stays on "" even after fetching typologies.
    useEffect(() => {
        if (["succeeded", "failed"].includes(typologiesStatus) || newTypologies.length > 0) return;
        dispatch(fetchTypologies()).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled" && mode === "new") {
                setValue("accountingTypology", (resp.payload as any).data[0]);
            }
        });
        setNewTypologies(typologies);
    }, [typologies, typologiesStatus]);

    const handleCreationEntry = (name: string) => {
        setNewTypologies((newTypologies) => [...newTypologies, name]);
    };

    useEffect(() => {
        if (usesStatementTemplates && !partnerStatements.length) dispatch(fetchPartnerStatements());
    }, [dispatch]);

    useEffect(() => {
        if (usesBrands) {
            dispatch(fetchBrands(true));
        }
    }, [usesBrands]);

    const formatBankingMetadata = (bm: Record<string, string> | null | undefined, bankingStatus?: string) => {
        if (!bm || Array.isArray(bm)) return null;
        const freeTextFields = new Set(["intl_bank_name"]);
        let entries = Object.entries(bm)
            .map(([k, v]): [string, string] => [
                k,
                typeof v === "string" ? (freeTextFields.has(k) ? v.trim() : v.replace(/[\s-]/g, "")) : v,
            ])
            .filter(([, v]) => v !== "" && v != null);
        if (bankingStatus === "local") entries = entries.filter(([k]) => !k.startsWith("intl_"));
        const filled = Object.fromEntries(entries);
        return Object.keys(filled).length > 0 ? filled : null;
    };

    /**
     * Format brandAccountingCode data for submit req
     */
    const formatBrandAccountingCode = (brandAccountingCode: BrandAccountingCode[]) => {
        const reqObj = {};
        brandAccountingCode.forEach((brand) => {
            if (brand.accountingCode.length) {
                reqObj[brand.brandId] = brand.accountingCode;
            }
        });
        return reqObj;
    };

    /** ON SUBMIT FORM */
    const onSubmit: SubmitHandler<TFormCreateBeneficiar> = async (form: TFormCreateBeneficiar) => {
        setSubmitFormStatus("pending");

        const resolvedBankingStatus = (() => {
            if (!form.bankAccountCountry?.code) return form.bankingStatus;
            const rules = countryFeatures.bankingStatusRules;
            if (rules?.length) {
                return resolveBankingStatus(rules, form.bankAccountCountry.code, companyCountryCode);
            }
            // No rules (CA, UK…): foreign country = foreign_local, same country = local
            return form.bankAccountCountry.code.toUpperCase() !== companyCountryCode.toUpperCase()
                ? "foreign_local"
                : "local";
        })();

        /**
         * Returns fields only linked to entityType (so if they filled field then change type, these are not saved).
         */
        const customFieldsData = form.customFields?.filter((item) => {
            const fieldData = customFields.find((field) => field.uuid === item.uuid);
            if (fieldData) {
                return fieldData.entityType === entityType || fieldData.entityType === "none";
            }
        });

        // Because the field of street is an autocomplete,
        // if they selected an option, the value is an object.
        if (isObject(form.street)) {
            form.street = (form.street as TAddressAutococompleteOption).street;
        }

        const vatChoice = form.vat?.value ?? BeneficiaryVAT.RETENUE_SOURCE;

        const req = {
            avatarUrl: form?.avatarUrl ?? "",
            name: form.name?.trim() ?? "",
            surname: form.surname?.trim() ?? "",
            pseudonym: form.pseudonym?.trim() || null,
            email: isLoginEmailChanging ? beneficiar.email : form.email?.trim() || null,
            loginEmail: form.loginEmail?.trim() || null,
            // Send UTC midnight of the local calendar day the user picked so the backend
            // stores the intended day regardless of the user's timezone.
            birthDate: form.birthDate
                ? new Date(Date.UTC(form.birthDate.getFullYear(), form.birthDate.getMonth(), form.birthDate.getDate()))
                : null,
            phoneNumber: shouldDraft(beneficiar.phoneNumber, form.phoneNumber as string) ?? "",
            street: shouldDraft(beneficiar.street, form.street as string) ?? "",
            zipCode: shouldDraft(beneficiar.zipCode, form.zipCode) ?? "",
            city: shouldDraft(beneficiar.city, form.city) ?? "",
            country: form.country?.name ?? "",
            socialProfileType: form.socialProfileType?.type,
            vat: vatChoice,
            precount: form.precount?.value,
            activityType: form.activityType ?? "",
            socialSecurityNumber:
                shouldDraft(beneficiar.socialSecurityNumber, form.socialSecurityNumber.replace(/ /g, "")) ?? "",
            siretNumber: shouldDraft(beneficiar.siretNumber, form.siretNumber) ?? "",
            beneficiaryCode: form.beneficiaryCode ?? "",
            lang: form.lang ?? "",

            /** Payments */
            ribOwnerName: isPaymentActive("rib") ? form.ribOwnerName : beneficiar.ribOwnerName ?? "",
            // When rib is inactive, omit iban/swift/bankingMetadata from the payload entirely so
            // the backend does not re-run IBAN validation and accidentally reset validBankInfos.
            ...(isPaymentActive("rib")
                ? {
                      iban: form.iban.replace(/ /g, ""),
                      swift: form.swift.replace(/ /g, ""),
                      bypassIbanValidation: form.bypassIbanValidation,
                      bankingMetadata: formatBankingMetadata(
                          {
                              ...(form.bankingMetadata ?? {}),
                              ...(form.bankAccountCountry?.code
                                  ? { bank_account_country: form.bankAccountCountry.code }
                                  : {}),
                          },
                          resolvedBankingStatus,
                      ),
                      bankingStatus: resolvedBankingStatus,
                  }
                : {}),
            paypalEmail: isPaymentActive("paypal") ? form.paypalEmail : "",
            bankCheck: isPaymentActive("check") ? 1 : 0,
            acceptedPayments: acceptedPayments.toString().replaceAll(",", " "), // ["rib paypal check"]
            accountingTypology: form.accountingTypology ?? "Par défaut",
            accountingCode: form.accountingCode ?? "",
            accountingCodeGroups: (JSON.parse(form.accountingCodeGroups ?? "[]") as BeneficiaryAuxAccountMap[])
                .map((o) => ({
                    mainAccount: o.mainAccount?.trim(),
                    auxiliaryAccount: o.auxiliaryAccount?.trim(),
                }))
                .filter((o) => o.mainAccount || o.auxiliaryAccount),
            statementCurrency: form.statementCurrency ? form.statementCurrency.code : companyInfos?.currency,
            customStatementTemplateId: (form.customStatementTemplate as IStatementTemplate)?.id ?? null,
            routingNumber: form.routingNumber,
            brandAccountingCode: formatBrandAccountingCode(form.brandAccountingCode as BrandAccountingCode[]),
            customFields: formatCustomFields(customFieldsData, currentCustomFields),
            entity: form.entity,
            withheldPercent: vatChoice === BeneficiaryVAT.FRANCHISE ? Math.round((form.withheldPercent ?? 0) * 100) : 0,
        };

        if (isPaymentActive("rib") && countryFeatures.bankingFields.includes("iban") && !form.bypassIbanValidation) {
            let hasIbanErrors = false;
            const ibanVal = req.iban;
            const bicVal = req.swift;
            if (ibanVal && !isValidIBAN(ibanVal)) {
                setError("iban", { message: t("CompanyBankModal.IbanError") });
                hasIbanErrors = true;
            }
            if (bicVal && !isValidBIC(bicVal)) {
                setError("swift", { message: t("CompanyBankModal.SwiftError") });
                hasIbanErrors = true;
            }
            if (hasIbanErrors) {
                setSubmitFormStatus("idle");
                return;
            }
        }

        const rawMeta = form.bankingMetadata as Record<string, string> | null;
        if (isPaymentActive("rib") && rawMeta && !Array.isArray(rawMeta)) {
            if (validateBankingMetadata(rawMeta, countryFeatures, setError, t)) {
                setSubmitFormStatus("idle");
                return;
            }
        }

        if (mode === "new") {
            const response = await dispatch(fetchCreateBeneficiary(req));
            // Payload is undefined on network errors, and message can be an array of validation errors
            const message = String(response.payload?.message ?? "");

            if (response.meta.requestStatus === "fulfilled") {
                if (handleModalClose)
                    handleModalClose("beneficiar", {
                        name: `${form.surname.toUpperCase()} ${form.name}`,
                        id: response.payload.data.id,
                    });
                setSubmitFormStatus("succeeded");
            }
            if (response.meta.requestStatus === "rejected") {
                setSubmitFormStatus("failed");
                if (message.includes("EmailAlreadyInUse")) {
                    setError("email", { message: t("NewBeneficiary.EmailExists") }, { shouldFocus: true });
                }
                if (message.toString().includes("email") && message.toString().includes("format")) {
                    setError("email", { message: t("NewBeneficiary.InvalidMail") }, { shouldFocus: true });
                }
                if (message?.startsWith?.("ServerError.Field.bankingMetadata.")) {
                    const fieldPath = response.payload?.data?.mappedKey as string;
                    if (fieldPath) setError(fieldPath as any, { message: t(message) });
                }
                if (message === "ServerError.Field.siretNumber") {
                    setError("siretNumber", { message: t(message) }, { shouldFocus: true });
                }
                if (message === "ServerError.Field.socialSecurityNumber") {
                    setError("socialSecurityNumber", { message: t(message) }, { shouldFocus: true });
                }
            }
        }

        if (mode === "edit-page") {
            const response = await dispatch(updateBeneficiary({ data: req, id: beneficiar.id }));
            // Payload is undefined on network errors, and message can be an array of validation errors
            const message = String(response.payload?.message ?? "");

            if (response.meta.requestStatus === "fulfilled") {
                if ([EPortalType.PRESS, EPortalType.AUDIOVISUAL].includes(portalType as EPortalType)) {
                    const { updatedRelatedResources } = response.payload;
                    if (updatedRelatedResources?.length) {
                        dispatch(updateArticles(updatedRelatedResources));
                    }
                }
                closeEditMode && closeEditMode();
                setTimeout(() => {
                    setSubmitFormStatus("succeeded");
                }, 300);
            }
            if (response.meta.requestStatus === "rejected") {
                setSubmitFormStatus("failed");
                if (message.includes("EmailAlreadyInUse")) {
                    setError("email", { message: t("NewBeneficiary.EmailExists") }, { shouldFocus: true });
                }
                if (message.toString().includes("email") && message.toString().includes("format")) {
                    setError("email", { message: t("NewBeneficiary.InvalidMail") }, { shouldFocus: true });
                }
                if (message?.startsWith?.("ServerError.Field.bankingMetadata.")) {
                    const fieldPath = response.payload?.data?.mappedKey as string;
                    if (fieldPath) setError(fieldPath as any, { message: t(message) });
                }
                if (message === "ServerError.Field.siretNumber") {
                    setError("siretNumber", { message: t(message) }, { shouldFocus: true });
                }
                if (message === "ServerError.Field.socialSecurityNumber") {
                    setError("socialSecurityNumber", { message: t(message) }, { shouldFocus: true });
                }
            }
        }
    };

    useEffect(() => {
        // same problem as in StatementForm, interact with data in store block navigate()
        if (submitFormStatus === "succeeded" && !handleModalClose) {
            navigate("/beneficiars");
            setSubmitFormStatus("idle");
        }
    }, [submitFormStatus]);

    useEffect(() => {
        if (["idle", "pending"].includes(customFieldsStatus)) {
            dispatch(fetchCustomFields()).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setCustomFields(resp.payload);
                    const newCustomFields = formatFieldsForDefaultValues({
                        customFields: resp.payload.filter((item) => item.relatedResource === "beneficiary"),
                        editModeActive,
                        typeData: beneficiar,
                    });
                    setValue("customFields", newCustomFields);
                }
            });
        }
    }, [customFieldsStatus]);

    const steps: IFormStep[] = [
        {
            stepTitle: t("NewBeneficiary.PersonalInformation"),
            stepSubtitle: "",
            conditionToDisable: watch("surname") === "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <PersonalInfoStep
                    useFormData={{ control, errors, watch, setValue }}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={0}
                    mode={mode}
                    customFields={customFields.filter((field) => field.formLocation === "personalInfo")}
                    isLoginEmailChanging={isLoginEmailChanging}
                    hasPendingEmail={hasPendingEmail}
                    pendingEmailValue={beneficiar?.pendingEmail}
                    accountStatus={beneficiar.accountStatus}
                    watchedLoginEmail={watchedLoginEmail}
                />
            ),
        },
        {
            stepTitle: t("Beneficiars.Address"),
            stepSubtitle: "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <AdressStep
                    useFormData={{ control, errors, setValue, watch }}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={1}
                />
            ),
        },
        {
            stepTitle: t("NewBeneficiary.Profile"),
            stepSubtitle: "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <ProfileStep
                    useFormData={{ control, errors, watch }}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={2}
                />
            ),
        },
        {
            stepTitle: t("NewBeneficiary.AccountingPayments"),
            stepSubtitle: "",
            format: (stepTitle: string, stepSubtitle: string) => (
                <AccountingPaymentStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={3}
                    acceptedPayments={acceptedPayments}
                    setAcceptedPayments={setAcceptedPayments}
                    mode={mode}
                    beneficiaryData={beneficiar}
                    showRibError={showRibError}
                    setShowRibError={setShowRibError}
                    companyInfos={companyInfos}
                    usesMultipleCurrencies={usesMultipleCurrencies}
                    alternativeCurrencies={alternativeCurrencies}
                    usesBrands={!["audiovisual", "press"].includes(companyInfos?.publisherType ?? "") && usesBrands}
                    brands={brands}
                    customFields={customFields.filter((field) => field.formLocation === "accounting")}
                    companyAcceptedPayments={companyAcceptedPayments}
                    isCompany={isCompany}
                    userRole={userRole}
                />
            ),
        },
        ...(usesStatementTemplates
            ? [
                  {
                      stepTitle: t("PartnerStatements.CustomStatement"),
                      stepSubtitle: t("PartnerStatements.CustomStatementDesc"),
                      format: (stepTitle: string, stepSubtitle: string) => (
                          <PartnerStatementStep
                              stepSubtitle={stepSubtitle}
                              stepTitle={stepTitle}
                              indexStep={4}
                              partnerStatements={partnerStatements}
                              mode={mode}
                          />
                      ),
                  },
              ]
            : []),
    ];

    /* NAVIGATION STEP SUBTITLE */
    const dataForSteps = [
        `${getValues("name")} ${getValues("surname")}`,
        `${getValues("zipCode")} ${getValues("country.name")}`,
        `${getValues("socialProfileType.description")?.split(" ")[0]}`,
    ];

    /** ARCHIVE STEP */
    const deleteStep = {
        title: t("NewBeneficiary.ArchiveBeneficiary"),
        subtitle: t(`NewBeneficiary.ArchiveDescription_${portalType}`),
    };

    const handleDeleteBeneficiary = async () => {
        await dispatch(fetchDeleteBeneficiary(beneficiar?.id));
    };

    useEffect(() => {
        if (deleteBeneficiaryStatus === "failed") {
            dispatch(setDeleteBeneficiaryIdleStatus());
            setShowModalDelete(false);
        }
        if (deleteBeneficiaryStatus === "succeeded") {
            dispatch(setDeleteBeneficiaryIdleStatus());
            navigate("/beneficiars");
            setShowModalDelete(false);
            closeEditMode && closeEditMode();
        }
    }, [deleteBeneficiaryStatus]);

    const deleteConfirmationDescription = (
        <>
            <p>{t(`Beneficiars.AssociatedContracts_${portalType}`)}</p>
            <p>{t(`Beneficiars.LinkedData_${portalType}`)}</p>
        </>
    );

    return (
        <>
            {/* This is the component that can be reused for every form. */}
            <FormProvider {...methods}>
                <div className="create-beneficiar-container">
                    <NewForm
                        type="beneficiary"
                        mode={mode}
                        steps={steps}
                        onSubmit={onSubmit}
                        useFormData={{ trigger, handleSubmit, isDirty, dirtyFields }}
                        dataForSteps={dataForSteps}
                        deleteStep={deleteStep}
                        onDelete={() => {
                            setShowModalDelete(true);
                        }}
                        returnTo={t("NewBeneficiary.ToBeneficiaries")}
                        txtBtnSubmitNew={t("NewBeneficiary.CreateBeneficiary")}
                        submitFormStatus={submitFormStatus}
                        closeEditMode={closeEditMode}
                        editModeActive={editModeActive}
                        handleScrollToErrors={() => handleScrollToErrors(errors)}
                        infoLink={{
                            type: t("Beneficiars.Beneficiar").toLowerCase(),
                            link:
                                portalType === EPortalType.BOOK
                                    ? "https://helpdesk.crealo.app/fr/article/creer-un-nouveau-beneficiaire-jekeqi/"
                                    : "https://presse-helpdesk.crealo.app/fr/article/creer-un-nouveau-beneficiaire-3tfeu4/",
                        }}
                    />
                </div>
            </FormProvider>

            {/** CREATE TYPOLOGY MODAL */}
            <ModalBox
                open={accountingModal}
                onClose={() => setAccountingModal(false)}
                mainTitle={t("Accounting.AddEntryType")}
            >
                <CreateTypology
                    close={() => setAccountingModal(false)}
                    onCreate={(name: string) => {
                        handleCreationEntry(name);
                        setNewTypologyName(name);
                    }}
                />
            </ModalBox>

            {/** BENEFICIAR DELETE MODAL */}
            {mode === "edit-page" && (
                <DeleteModal
                    open={showModalDelete}
                    onClose={() => setShowModalDelete(false)}
                    elementTitle={`${beneficiar?.surname.toUpperCase()} ${beneficiar?.name ?? ""}`}
                    onDelete={handleDeleteBeneficiary}
                    needsConfirmation={termAssociatedToBeneficiaries.length > 0}
                    confirmationText={t("Beneficiars.DeleteBeneficiary")}
                    description={
                        termAssociatedToBeneficiaries.length > 0
                            ? deleteConfirmationDescription
                            : t(`Beneficiars.NoAssociatedContracts_${portalType}`)
                    }
                />
            )}
        </>
    );
};

export default BeneficiaryFormContainer;
